import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FlightDetailModal from './FlightDetailModal';
import HotelDetailModal from './HotelDetailModal';
import TransportDetailModal from './TransportDetailModal';
import { getDataAPI, deleteDataAPI } from '../../../utils/fetchData';
import ApprovalModal from '../../../components/ApprovalModal';
import { useApproval } from '../../../hooks/useApproval';
import { canEditApp } from '../../../utils/permissions';

const TravelTab = ({ show, onRefresh }) => {
  const navigate = useNavigate();
  const { approvalModal, openApprovalModal, closeApprovalModal, sendTourForApproval } = useApproval();
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [tourPlanId, setTourPlanId] = useState(null);
  const [checkingTourStatus, setCheckingTourStatus] = useState(true);
  
  // Selection state for multi-select functionality
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedTransports, setSelectedTransports] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    checkTourStatus();
  }, [show._id]);

  // Re-check tour status when tab becomes visible (user returns from creating tour)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
                        console.log('Tab visible again, re-checking tour status');
        checkTourStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Re-check when onRefresh is called
  useEffect(() => {
    if (onRefresh) {
      checkTourStatus();
    }
  }, [onRefresh]);

  const checkTourStatus = async () => {
    try {
      setCheckingTourStatus(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      console.log(`Checking tour status for show: ${show._id}`);
      const response = await getDataAPI('user/tm/shows/tour-status', token);
      
      console.log('Tour status response:', response.data);
      
      if (response.data.status === 200 && response.data.data.showTourMap) {
        const tourInfo = response.data.data.showTourMap[show._id];
        setTourPlanId(tourInfo?.tourPlanId || null);
        
        if (tourInfo) {
          console.log(`Show ${show._id} is in tour: ${tourInfo.tourPlanId} (${tourInfo.tourPlanName})`);
        } else {
          console.log(`Show ${show._id} is NOT in any tour`);
          console.log('Available tours:', Object.keys(response.data.data.showTourMap).length);
        }
      }
    } catch (error) {
      console.error('Error checking tour status:', error);
      setTourPlanId(null);
    } finally {
      setCheckingTourStatus(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getAirlineIcon = (airlineName) => {
    // Return empty string instead of emojis
    return '';
  };

  const getAirlineName = (flight) => {
    // Handle both old format (string) and new format (object)
    if (typeof flight.airline === 'string') {
      return flight.airline;
    }
    return flight.airline?.name || 'Unknown Airline';
  };

  const getDepartureAirport = (flight) => {
    return flight.departure?.airport || flight.origin || 'XXX';
  };

  const getArrivalAirport = (flight) => {
    return flight.arrival?.airport || flight.destination || 'XXX';
  };

  const getDepartureTime = (flight) => {
    return flight.departure?.time || flight.departure_time;
  };

  const formatDuration = (flight) => {
    // If duration is in minutes
    if (flight.duration && typeof flight.duration === 'number') {
      const hours = Math.floor(flight.duration / 60);
      const minutes = flight.duration % 60;
      return `${hours}h ${minutes}m`;
    }
    return flight.flight_duration || 'N/A';
  };

  const getCabinClass = (flight) => {
    return flight.seatInfo?.class || flight.cabin_class || 'Economy';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'option': { label: 'OPTION', color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.1)' },
      'selected': { label: 'SELECTED', color: '#FF9500', bg: 'rgba(255,149,0,0.2)' },
      'booked': { label: 'BOOKED', color: '#34C759', bg: 'rgba(52,199,89,0.2)' },
      'cancelled': { label: 'CANCELLED', color: '#FF3B30', bg: 'rgba(255,59,48,0.2)' }
    };
    return badges[status] || badges['option'];
  };

  const formatDateBadge = (dateString) => {
    if (!dateString) return { month: 'TBD', day: '?' };
    
    try {
      // Handle YYYY-MM-DD format directly to avoid timezone issues
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const monthName = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
        return { month: monthName, day: parseInt(day) };
      }
      
      // Fallback for other date formats
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const day = date.getDate();
      return { month, day };
    } catch {
      const parts = dateString.split(' ');
      if (parts.length >= 2) {
        return { 
          month: parts[0].substring(0, 3).toUpperCase(), 
          day: parts[1].replace(',', '') 
        };
      }
      return { month: 'TBD', day: '?' };
    }
  };

  const handleDeleteFlight = async (e, flightId) => {
    e.stopPropagation(); // Prevent opening flight detail modal
    
    if (!window.confirm('Are you sure you want to delete this flight?')) {
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      await deleteDataAPI(`user/tm/flights/${flightId}`, token);
      
      // Refresh show data to reflect deletion
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting flight:', error);
      alert('Failed to delete flight. Please try again.');
    }
  };

  const handleSendForApproval = (travelItem, itemType) => {
    const approvalData = {
      ...travelItem,
      showId: show._id,
      showName: show.venue_name || show.event_name,
      itemType: itemType
    };
    
    sendTourForApproval(approvalData, (approvalData) => {
      console.log('Travel approval sent successfully:', approvalData);
    });
  };

  // Selection handlers
  const handleFlightSelect = (flight) => {
    setSelectedFlights(prev => {
      const isSelected = prev.some(item => item._id === flight._id);
      if (isSelected) {
        return prev.filter(item => item._id !== flight._id);
      } else {
        return [...prev, flight];
      }
    });
  };

  const handleHotelSelect = (hotel) => {
    setSelectedHotels(prev => {
      const isSelected = prev.some(item => item._id === hotel._id);
      if (isSelected) {
        return prev.filter(item => item._id !== hotel._id);
      } else {
        return [...prev, hotel];
      }
    });
  };

  const handleTransportSelect = (transport) => {
    setSelectedTransports(prev => {
      const isSelected = prev.some(item => item._id === transport._id);
      if (isSelected) {
        return prev.filter(item => item._id !== transport._id);
      } else {
        return [...prev, transport];
      }
    });
  };

  const handleBulkDelete = async () => {
    const allSelected = [...selectedFlights, ...selectedHotels, ...selectedTransports];
    if (allSelected.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${allSelected.length} selected travel item(s)?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Delete flights
      for (const flight of selectedFlights) {
        await deleteDataAPI(`user/tm/flights/${flight._id}`, token);
      }
      
      // Delete hotels (assuming similar API)
      for (const hotel of selectedHotels) {
        await deleteDataAPI(`user/tm/hotels/${hotel._id}`, token);
      }
      
      // Delete transport (assuming similar API)
      for (const transport of selectedTransports) {
        await deleteDataAPI(`user/tm/transport/${transport._id}`, token);
      }
      
      // Clear selections
      setSelectedFlights([]);
      setSelectedHotels([]);
      setSelectedTransports([]);
      
      // Refresh data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting travel items:', error);
      alert('Failed to delete some travel items. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getTotalSelected = () => {
    return selectedFlights.length + selectedHotels.length + selectedTransports.length;
  };

  // Show loading state while checking tour status
  // Removed loading state - data should be preloaded
  // if (checkingTourStatus) {
  //   return (
  //     <div className="px-4 flex items-center justify-center py-12">
  //       <div className="text-[rgba(255,255,255,0.5)] text-[11px]">Loading...</div>
  //     </div>
  //   );
  // }

  // Always show travel sections - tours are optional
  return (
    <div className="px-4 space-y-6 pb-6">
      {/* Optional Tour Info Banner */}
      {tourPlanId && (
        <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-3 mb-4">
          <p className="text-[rgba(255,255,255,0.7)] text-[10px] text-left">
            This show is part of a tour - travel can be coordinated with other shows
          </p>
        </div>
      )}

      {/* Flights Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white text-[12px] font-semibold">Flights</h3>
          <div className="flex items-center gap-2">
            {selectedFlights.length > 0 && (
              <>
                <span className="text-white text-[10px]">
                  Selected ({selectedFlights.length})
                </span>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="text-red-500 text-[10px] cursor-pointer hover:opacity-70 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
            <button
              onClick={() => navigate('/tour-manager/flight-search', { 
                state: { 
                  showId: show._id, 
                  selectedShows: [show], // Pass the current show as selected
                  tourPlanId: tourPlanId || undefined 
                } 
              })}
              className="bg-[rgba(255,149,0,0.2)] text-[#FF9500] border-none rounded-lg px-3 py-1 text-[10px] font-semibold cursor-pointer transition-colors hover:bg-[rgba(255,149,0,0.3)]"
            >
              Search Flights
            </button>
          </div>
        </div>
        {!show.flights || show.flights.length === 0 ? (
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 text-center">
            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">No flights added yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {show.flights.map((flight, index) => {
              const airlineName = getAirlineName(flight);
              const departureAirport = getDepartureAirport(flight);
              const arrivalAirport = getArrivalAirport(flight);
              const departureTime = getDepartureTime(flight);
              const duration = formatDuration(flight);
              const cabinClass = getCabinClass(flight);
              const statusBadge = getStatusBadge(flight.status || 'option');
              const dateBadge = formatDateBadge(departureTime);
              
              return (
                <div
                  key={flight._id || index}
                  onClick={() => handleFlightSelect(flight)}
                  className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                    selectedFlights.some(item => item._id === flight._id)
                      ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Date Section */}
                    <div className="flex flex-col items-center min-w-[60px]">
                      <span className="text-[#FFFF00] text-[12px] font-normal">{dateBadge.month}</span>
                      <span className="text-[#FFFF00] text-[18px] font-normal">{dateBadge.day}</span>
                    </div>
                    
                    {/* Flight Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white text-[12px] font-normal">
                          {departureAirport} to {arrivalAirport}
                        </h3>
                        <div className="flex items-center gap-2">
                          {canEditApp('tourmngr') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendForApproval(flight, 'flight');
                              }}
                              className="text-[#FF6B35] text-[10px] px-2 py-1 hover:bg-[rgba(255,107,53,0.1)] rounded transition-colors"
                            >
                              Submit
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFlight(flight);
                            }}
                            className="text-[#FF9500] text-[10px] px-2 py-1 hover:bg-[rgba(255,149,0,0.1)] rounded transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                        {airlineName} • {formatTime(departureTime)}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                          {duration}
                        </span>
                        <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded capitalize">
                          {cabinClass}
                        </span>
                        <span 
                          className="px-2 py-1 rounded text-[9px] font-bold tracking-wider"
                          style={{
                            color: statusBadge.color,
                            backgroundColor: statusBadge.bg
                          }}
                        >
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hotels Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white text-[12px] font-semibold">Hotels</h3>
          <div className="flex items-center gap-2">
            {selectedHotels.length > 0 && (
              <>
                <span className="text-white text-[10px]">
                  Selected ({selectedHotels.length})
                </span>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="text-red-500 text-[10px] cursor-pointer hover:opacity-70 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
            <button
              onClick={() => navigate('/tour-manager/hotel-search', { 
                state: { 
                  showId: show._id, 
                  selectedShows: [show], // Pass the current show as selected
                  tourPlanId: tourPlanId || undefined 
                } 
              })}
              className="bg-[rgba(255,149,0,0.2)] text-[#FF9500] border-none rounded-lg px-3 py-1 text-[10px] font-semibold cursor-pointer transition-colors hover:bg-[rgba(255,149,0,0.3)]"
            >
              Search Hotels
            </button>
          </div>
        </div>
        {!show.hotels || show.hotels.length === 0 ? (
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 text-center">
            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">No hotels added yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {show.hotels.map((hotel, index) => {
              const statusBadge = getStatusBadge(hotel.status || 'option');
              
              return (
                <div
                  key={hotel._id || index}
                  onClick={() => handleHotelSelect(hotel)}
                  className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedHotels.some(item => item._id === hotel._id)
                      ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Date Section */}
                    <div className="flex flex-col items-center min-w-[60px]">
                      {(() => {
                        const checkInDate = hotel.checkIn || hotel.check_in_date || hotel.check_in;
                        const hotelDateBadge = formatDateBadge(checkInDate);
                        return (
                          <>
                            <span className="text-[#FFFF00] text-[12px] font-normal">{hotelDateBadge.month}</span>
                            <span className="text-[#FFFF00] text-[18px] font-normal">{hotelDateBadge.day}</span>
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Hotel Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white text-[12px] font-normal">
                          {hotel.hotelName || hotel.hotel_name}
                        </h3>
                        <div className="flex items-center gap-2">
                          {canEditApp('tourmngr') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendForApproval(hotel, 'hotel');
                              }}
                              className="text-[#FF6B35] text-[10px] px-2 py-1 hover:bg-[rgba(255,107,53,0.1)] rounded transition-colors"
                            >
                              Submit
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedHotel(hotel);
                            }}
                            className="text-[#FF9500] text-[10px] px-2 py-1 hover:bg-[rgba(255,149,0,0.1)] rounded transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                      {hotel.chain && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">
                          {hotel.chain}
                        </p>
                      )}
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">
                        {((hotel.address?.city || hotel.city) && (hotel.address?.country || hotel.country)) 
                          ? `${hotel.address?.city || hotel.city}, ${hotel.address?.country || hotel.country}`
                          : (hotel.address?.city || hotel.city || hotel.address?.country || hotel.country || 'Location TBD')}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {hotel.checkIn && (
                          <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                            In: {formatTime(hotel.checkIn)}
                          </span>
                        )}
                        {hotel.checkOut && (
                          <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                            Out: {formatTime(hotel.checkOut)}
                          </span>
                        )}
                        {hotel.pricing?.perNight && (
                          <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                            ${hotel.pricing.perNight}/night
                          </span>
                        )}
                        <span 
                          className="px-2 py-1 rounded text-[9px] font-bold tracking-wider"
                          style={{
                            color: statusBadge.color,
                            backgroundColor: statusBadge.bg
                          }}
                        >
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ground Transport Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white text-[12px] font-semibold">Ground Transport</h3>
          <div className="flex items-center gap-2">
            {selectedTransports.length > 0 && (
              <>
                <span className="text-white text-[10px]">
                  Selected ({selectedTransports.length})
                </span>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="text-red-500 text-[10px] cursor-pointer hover:opacity-70 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
            <button
              onClick={() => navigate(`/tour-manager/add-ground-transport/${show._id}`)}
              className="bg-[rgba(255,149,0,0.2)] text-[#FF9500] border-none rounded-lg px-3 py-1 text-[10px] font-semibold cursor-pointer transition-colors hover:bg-[rgba(255,149,0,0.3)]"
            >
              Add Transport
            </button>
          </div>
        </div>
        {!show.ground_transport || show.ground_transport.length === 0 ? (
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 text-center">
            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">No transport added yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {show.ground_transport.map((transport, index) => {
              // Handle both old and new data structure
              const pickupTime = transport.pickup?.time || transport.pickup_time;
              const dropoffLocation = transport.dropoff?.location || transport.destination || 'To Venue';
              const vehicleType = transport.vehicleInfo?.type || transport.vehicle_type || transport.type || 'Ground Transport';
              const statusBadge = getStatusBadge(transport.status || 'option');
              
              return (
                <div
                  key={transport._id || index}
                  onClick={() => handleTransportSelect(transport)}
                  className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedTransports.some(item => item._id === transport._id)
                      ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[#FF9500] hover:bg-[rgba(255,149,0,0.05)]'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Transport Icon Section */}
                    <div className="flex flex-col items-center min-w-[60px]">
                      <span className="text-[#FFFF00] text-[12px] font-normal">RIDE</span>
                      <span className="text-[#FFFF00] text-[14px] font-normal">•</span>
                    </div>
                    
                    {/* Transport Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white text-[12px] font-normal">
                          {vehicleType}
                        </h3>
                        <div className="flex items-center gap-2">
                          {canEditApp('tourmngr') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendForApproval(transport, 'transport');
                              }}
                              className="text-[#FF6B35] text-[10px] px-2 py-1 hover:bg-[rgba(255,107,53,0.1)] rounded transition-colors"
                            >
                              Submit
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTransport(transport);
                            }}
                            className="text-[#FF9500] text-[10px] px-2 py-1 hover:bg-[rgba(255,149,0,0.1)] rounded transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          {pickupTime && (
                            <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                              Pickup: {formatTime(pickupTime)}
                            </span>
                          )}
                          {dropoffLocation && (
                            <span className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] text-[10px] px-2 py-1 rounded">
                              To: {dropoffLocation}
                            </span>
                          )}
                        </div>
                        <span 
                          className="px-2 py-1 rounded text-[9px] font-bold tracking-wider"
                          style={{
                            color: statusBadge.color,
                            backgroundColor: statusBadge.bg
                          }}
                        >
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedFlight && (
        <FlightDetailModal
          flight={selectedFlight}
          show={show}
          onClose={() => setSelectedFlight(null)}
          onRefresh={onRefresh}
        />
      )}
      {selectedHotel && (
        <HotelDetailModal
          hotel={selectedHotel}
          show={show}
          onClose={() => setSelectedHotel(null)}
          onRefresh={onRefresh}
        />
      )}
      {selectedTransport && (
        <TransportDetailModal
          transport={selectedTransport}
          show={show}
          onClose={() => setSelectedTransport(null)}
          onRefresh={onRefresh}
        />
      )}

      {/* Approval Modal */}
      <ApprovalModal
        isOpen={approvalModal.isOpen}
        onClose={closeApprovalModal}
        itemData={approvalModal.itemData}
        itemType={approvalModal.itemType}
        onApprovalSent={approvalModal.onApprovalSent}
      />
    </div>
  );
};

export default TravelTab;

